import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Arc, Status } from '../plan/arc';
import { innerArcNames, openInside, planLine } from '../plan/arc';
import type { HeadedArc } from '../plan/chronology';
import { planShelves } from '../plan/grouping';
import { needsEvidence } from '../plan/edits';
import { arcHref } from '../routes/ArcRoute';
import { useProvideOverrideBar } from '../shell/overrideBar';
import { StatusGlyph } from './StatusGlyph';
import { StatusPicker } from './StatusPicker';
import { useBoardKeys } from './useBoardKeys';
import { SelectionBar } from './SelectionBar';
import { RowMenu } from './RowMenu';
import { AddToFlow } from '../membership/AddDialogs';
import { usePublishSelection } from '../membership/selection';
import type { Item } from '../membership/membership';
import { CardTagMark } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';

/**
 * The plan, in one of its two readings.
 *
 * `recent` is the plan as one run, newest first, each arc with the one line
 * that says what to do about it. `shelves` is what is in play and queued,
 * grouped by the storylines that hold it. Browser-local: the phone has no
 * opinion about how this browser draws the plan.
 */
export type Layout = 'recent' | 'shelves';

const LAYOUT_KEY = 'plan.layout';

export function readLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'shelves' ? 'shelves' : 'recent';
  } catch {
    return 'recent';
  }
}

export function writeLayout(layout: Layout): void {
  try {
    localStorage.setItem(LAYOUT_KEY, layout);
  } catch {
    /* the reading is not remembered, and nothing else is lost */
  }
}

export function Desk({
  arcs,
  layout,
  headed,
  onSetStatus,
  onSetArchived,
  onNudge,
  onGroup,
  onMembership,
  allArcs,
  markOf,
}: {
  /** What is shown — filtered, archived ones only when asked for. */
  arcs: Arc[];
  layout: Layout;
  /** The same arcs as one run, newest first — what `recent` draws. */
  headed: HeadedArc[];
  onSetStatus: (arc: Arc, status: Status, evidence?: string) => void;
  onSetArchived?: (arc: Arc, archived: boolean) => void;
  onNudge: (arc: Arc, direction: -1 | 1) => void;
  onGroup: (name: string, members: Arc[]) => Promise<void>;
  onMembership: (storyline: Arc, member: Arc, join: boolean) => Promise<void>;
  allArcs: Arc[];
  /** An arc's tag mark, at the end of its name's line. */
  markOf?: (arc: Arc) => TagMark | null;
}) {
  // The rect of whatever opened it rides along, because the picker renders as a
  // sibling of the whole plan and cannot find the row on its own.
  const [picking, setPicking] = useState<{ arc: Arc; anchor: DOMRect | null } | null>(null);
  const pick = (arc: Arc, anchor?: DOMRect) => setPicking({ arc, anchor: anchor ?? null });
  // A keystroke has no rect of its own, so the focused arc's glyph is found in
  // the DOM — by attribute, since an arc name is free text.
  const pickAtRow = (arc: Arc) => {
    const row = [...document.querySelectorAll('[data-row-name]')].find(
      (el) => el.getAttribute('data-row-name') === arc.name,
    );
    pick(arc, row?.querySelector('.status-button')?.getBoundingClientRect());
  };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<Arc[] | null>(null);
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; arc: Arc } | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const navigate = useNavigate();

  const { shelves, loose } = useMemo(() => planShelves(arcs), [arcs]);

  // Focus walks the arcs in the order they are drawn; one on two shelves is
  // walked once.
  const focusable = useMemo(() => {
    if (layout === 'recent') return headed.map((h) => h.arc);
    const seen = new Set<string>();
    return [...shelves.flatMap((s) => s.arcs), ...loose].filter((arc) =>
      seen.has(arc.name) ? false : (seen.add(arc.name), true),
    );
  }, [layout, headed, shelves, loose]);

  useBoardKeys({
    rows: focusable,
    focused,
    setFocused,
    // Never write an invalid file from a keystroke: `done` without evidence
    // opens the picker to ask, exactly as choosing it would.
    onStatus: (arc, status) =>
      status === 'done' && needsEvidence(arc) ? pickAtRow(arc) : onSetStatus(arc, status),
    // Nothing is arranged by hand in the chronology, so ⌥↑/↓ has nothing to move there.
    onNudge: layout === 'recent' ? () => {} : onNudge,
    onOpen: (arc) => navigate(arcHref(arc.name)),
    // esc clears a selection first — the louder state goes before focus.
    onEscape: () => (selected.size > 0 ? setSelected(new Set()) : undefined),
    enabled: picking === null,
  });

  const chosen = focusable.filter((a) => selected.has(a.name));
  const chosenItems = useMemo(() => chosen.map(asItem), [chosen.map((a) => a.path).join('\n')]);
  usePublishSelection(chosenItems, () => setSelected(new Set()));
  useProvideOverrideBar(
    selected.size > 0 ? (
      <SelectionBar
        count={selected.size}
        canGroup={selected.size >= 2}
        onGroup={async (name) => {
          await onGroup(name, chosen);
          setSelected(new Set());
        }}
        onAddTo={() => setAdding(chosen)}
        onClear={() => setSelected(new Set())}
      />
    ) : null,
  );

  /** Repeated nudges rather than a new write path — one definition of what
   *  a move means, reused. */
  const onNudgeToTop = (arc: Arc) => {
    const band = focusable.filter((a) => a.status === arc.status);
    const at = band.findIndex((a) => a.name === arc.name);
    for (let i = 0; i < at; i++) onNudge(arc, -1);
  };

  /** A click opens the arc; shift-click gathers it into a selection instead,
   *  since the two share a button and the modifier is what tells them apart. */
  const press = (arc: Arc) => (event: MouseEvent) => {
    if (event.shiftKey) {
      event.preventDefault();
      setSelected((s) => {
        const next = new Set(s);
        if (next.has(arc.name)) next.delete(arc.name);
        else next.add(arc.name);
        return next;
      });
      return;
    }
    navigate(arcHref(arc.name));
  };

  const menu = (arc: Arc) => (event: MouseEvent) => {
    event.preventDefault();
    setRowMenu({ x: event.clientX, y: event.clientY, arc });
  };

  const item = (arc: Arc) => ({
    arc,
    inside: openInside(arc, allArcs),
    mark: markOf?.(arc) ?? null,
    focused: focused === arc.name,
    selected: selected.has(arc.name),
    onPress: press(arc),
    onMenu: menu(arc),
  });

  return (
    <div className={`desk layout-${layout}`}>
      {layout === 'recent' ? (
        <div className="plan-recent">
          {headed.length === 0 && <p className="plan-quiet">Nothing here.</p>}
          {headed.map(({ head, arc }) => (
            <div key={arc.path}>
              {head && <h2 className="plan-day">{head}</h2>}
              <PlanRow {...item(arc)} />
            </div>
          ))}
        </div>
      ) : (
        <div className="plan-shelves">
          {shelves.length === 0 && loose.length === 0 && <p className="plan-quiet">Nothing in play.</p>}
          {shelves.map((shelf) => (
            <section key={shelf.storyline.path} className="plan-shelf">
              <h2 className="plan-shelf-head">
                <Link to={arcHref(shelf.storyline.name)}>{shelf.storyline.title}</Link>
              </h2>
              <div className="plan-grid">
                {shelf.arcs.map((arc) => (
                  <PlanCard key={arc.path} {...item(arc)} />
                ))}
              </div>
            </section>
          ))}
          {loose.length > 0 && (
            <section className="plan-shelf">
              {shelves.length > 0 && <h2 className="plan-shelf-head is-loose">On their own</h2>}
              <div className="plan-grid">
                {loose.map((arc) => (
                  <PlanCard key={arc.path} {...item(arc)} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {rowMenu && (
        <RowMenu
          at={rowMenu}
          arc={rowMenu.arc}
          heldBy={allArcs.find((a) => innerArcNames(a).includes(rowMenu.arc.name)) ?? null}
          onOpen={() => navigate(arcHref(rowMenu.arc.name))}
          onStatus={() => pick(rowMenu.arc, DOMRect.fromRect({ x: rowMenu.x, y: rowMenu.y }))}
          onSendToTop={() => onNudgeToTop(rowMenu.arc)}
          onAddTo={() => setAdding([rowMenu.arc])}
          onRemoveFrom={(s) => void onMembership(s, rowMenu.arc, false)}
          onClose={() => setRowMenu(null)}
        />
      )}

      {adding && <AddToFlow items={adding.map(asItem)} onDone={() => setSelected(new Set())} onClose={() => setAdding(null)} />}

      {picking && (
        <StatusPicker
          arc={picking.arc}
          anchor={picking.anchor}
          onClose={() => setPicking(null)}
          onPick={(status, evidence) => {
            onSetStatus(picking.arc, status, evidence);
            setPicking(null);
          }}
          onArchive={
            onSetArchived &&
            ((archived) => {
              onSetArchived(picking.arc, archived);
              setPicking(null);
            })
          }
        />
      )}
    </div>
  );
}

interface ItemProps {
  arc: Arc;
  /** A storyline's open arcs; zero for anything else. */
  inside: number;
  mark: TagMark | null;
  focused: boolean;
  selected: boolean;
  onPress: (event: MouseEvent) => void;
  onMenu: (event: MouseEvent) => void;
}

/** An arc in the run: its state, its name — with a storyline's open arcs
 *  beside it — and the one line that says what to do about it. */
function PlanRow({ arc, inside, mark, focused, selected, onPress, onMenu }: ItemProps) {
  const line = planLine(arc);
  return (
    <button
      type="button"
      className={`plan-row${focused ? ' focused' : ''}${selected ? ' selected' : ''}`}
      data-row-name={arc.name}
      onClick={onPress}
      onContextMenu={onMenu}
    >
      <span className="plan-glyph status-button">
        <StatusGlyph status={arc.status} size={10} />
      </span>
      <span className="plan-text">
        <span className="plan-row-name">
          {arc.title}
          {inside > 0 && <span className="plan-inside">{inside} inside</span>}
        </span>
        {line && <span className={`plan-row-line${line.asking ? ' is-asking' : ''}`}>{line.text}</span>}
      </span>
      {mark && (
        <span className="plan-mark">
          <CardTagMark mark={mark} size={13} />
        </span>
      )}
    </button>
  );
}

/** An arc on a shelf: its state and its name, "waiting on you" when it is —
 *  the only status said in words — and a storyline's open arcs. */
function PlanCard({ arc, inside, mark, focused, selected, onPress, onMenu }: ItemProps) {
  return (
    <button
      type="button"
      className={`plan-card${focused ? ' focused' : ''}${selected ? ' selected' : ''}`}
      data-row-name={arc.name}
      onClick={onPress}
      onContextMenu={onMenu}
    >
      <span className="plan-glyph status-button">
        <StatusGlyph status={arc.status} size={10} />
      </span>
      <span className="plan-text">
        <span className="plan-card-name">{arc.title}</span>
        {arc.status === 'needs-input' && <span className="plan-card-waiting">waiting on you</span>}
        {inside > 0 && <span className="plan-card-inside">{inside} inside</span>}
      </span>
      {mark && (
        <span className="plan-mark">
          <CardTagMark mark={mark} size={13} />
        </span>
      )}
    </button>
  );
}

const asItem = (arc: Arc): Item => ({ path: arc.path, anchor: null, kind: 'arc', label: arc.title });
